// Size chart helpers — extracted from product.js so they are available on every
// page (product detail, wishlist, and any grid that renders the quick-add drawer).
// Exposes window.sizeChartHelpers; product.js consumes the same global.

// Normalize size label for comparison (trim, lowercase)
const normalizeSizeLabel = (str) => (str || "").trim().toLowerCase();

// Modal size chart table tabs
const initSizeChartTabs = () => {
  const tabs = Array.from(document.querySelectorAll(".size-chart__tab"));
  const panes = Array.from(document.querySelectorAll(".size-chart__pane"));

  if (!tabs.length || !panes.length) {
    return;
  }

  const setActive = (tabId) => {
    tabs.forEach((tab) => {
      const isActive = tab.id === tabId;
      tab.classList.toggle("active", isActive);
      tab.setAttribute("aria-selected", String(isActive));
      tab.setAttribute("tabindex", isActive ? "0" : "-1");
    });

    panes.forEach((pane) => {
      const isActive = pane.id === `size-${tabId}`;
      pane.classList.toggle("is-active", isActive);
      pane.hidden = !isActive;
      pane.setAttribute("aria-hidden", String(!isActive));
    });
  };

  tabs.forEach((tab) => {
    tab.addEventListener("click", () => setActive(tab.id));

    tab.addEventListener("keydown", (event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
        return;
      }

      event.preventDefault();
      const currentIndex = tabs.indexOf(tab);
      const direction = event.key === "ArrowLeft" ? -1 : 1;
      const nextIndex = (currentIndex + direction + tabs.length) % tabs.length;
      const nextTab = tabs[nextIndex];
      setActive(nextTab.id);
      nextTab.focus();
    });
  });

  const initialTab =
    tabs.find((tab) => tab.classList.contains("active")) || tabs[0];
  if (initialTab) {
    setActive(initialTab.id);
  }
};

// Build size chart tabs from the source table inside [data-size-guide-source]
// Reads sizes from tbody th and generates .size-chart__tab buttons
// If data-product-sizes is set, only shows sizes that the product has
const initSizeChartTabsFromSource = () => {
  const modal = document.querySelector(".size-chart");
  const sourceContainer =
    (modal && modal.querySelector("[data-size-guide-source]")) ||
    document.querySelector("[data-size-guide-source]");
  const tabsContainer =
    (modal && modal.querySelector(".size-chart__tabs")) ||
    document.querySelector(".size-chart__tabs");

  if (!sourceContainer || !tabsContainer) return;

  const table = sourceContainer.querySelector("table");
  const tbody = table ? table.querySelector("tbody") : null;
  if (!table || !tbody) return;

  const rows = Array.from(tbody.querySelectorAll("tr"));
  if (!rows.length) return;

  const productSizesRaw = modal?.dataset?.productSizes || "";
  const productSizesSet = productSizesRaw
    ? new Set(
      productSizesRaw.split("|").map(normalizeSizeLabel).filter(Boolean),
    )
    : null;

  // Extract sizes from first th in each row, preserve order, dedupe
  // Filter to product sizes only when data-product-sizes is present
  const seen = new Set();
  const sizes = [];
  rows.forEach((row) => {
    const th = row.querySelector("th");
    const label = th ? th.textContent.trim() : "";
    if (!label || seen.has(label)) return;
    if (productSizesSet && !productSizesSet.has(normalizeSizeLabel(label)))
      return;
    seen.add(label);
    sizes.push(label);
  });

  if (!sizes.length) return;

  // Helper to create safe id from label
  const toId = (str) => {
    return str
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}+/gu, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  // Build buttons efficiently
  tabsContainer.innerHTML = "";
  const frag = document.createDocumentFragment();
  sizes.forEach((label, idx) => {
    const id = toId(label) || `size-${idx + 1}`;
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "size-chart__tab";
    btn.id = id;
    btn.setAttribute("role", "tab");
    btn.setAttribute("aria-controls", `size-${id}`);
    btn.setAttribute("aria-selected", idx === 0 ? "true" : "false");
    btn.setAttribute("tabindex", idx === 0 ? "0" : "-1");
    if (idx === 0) btn.classList.add("active");
    btn.textContent = label;
    frag.appendChild(btn);
  });

  tabsContainer.appendChild(frag);
};

// Build size chart panes from the source table inside [data-size-guide-source]
// Creates one pane per size with a table of measurements
// If data-product-sizes is set, only includes panes for sizes the product has
const buildSizeChartPanesFromSource = () => {
  const modal = document.querySelector(".size-chart");
  const sourceContainer =
    (modal && modal.querySelector("[data-size-guide-source]")) ||
    document.querySelector("[data-size-guide-source]");
  const contentContainer =
    (modal && modal.querySelector(".size-chart__content")) ||
    document.querySelector(".size-chart__content");

  if (!contentContainer) return;

  // Always clear content to remove any hardcoded pane
  contentContainer.innerHTML = "";

  const table = sourceContainer?.querySelector("table");
  const tbody = table?.querySelector("tbody");
  if (!tbody) return;

  const thead = table.querySelector("thead");
  const headerCells = thead
    ? Array.from(thead.querySelectorAll("tr th, tr td"))
    : [];

  // Determine measurement labels: prioritize table headers (from Shopify Admin Page)
  // over themes fixed translations (window.sizeChartStrings.measures)
  const tableHeaders =
    headerCells.length > 1
      ? headerCells
        .slice(1)
        .map((cell) => cell.textContent.trim())
        .filter((h) => h !== "")
      : [];

  const measureNames =
    tableHeaders.length > 0
      ? tableHeaders
      : window.sizeChartStrings?.measures || [];

  let rows = Array.from(tbody.querySelectorAll("tr"));
  const productSizesRaw = modal?.dataset?.productSizes || "";
  const productSizesSet = productSizesRaw
    ? new Set(
      productSizesRaw.split("|").map(normalizeSizeLabel).filter(Boolean),
    )
    : null;
  if (productSizesSet) {
    rows = rows.filter((row) => {
      const label = (row.querySelector("th")?.textContent || "").trim();
      return productSizesSet.has(normalizeSizeLabel(label));
    });
  }
  if (!rows.length) return;

  const toId = (str) => {
    return str
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}+/gu, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const frag = document.createDocumentFragment();

  rows.forEach((row, idx) => {
    const sizeLabel = (row.querySelector("th")?.textContent || "").trim();
    const id = toId(sizeLabel) || `size-${idx + 1}`;
    const values = Array.from(row.querySelectorAll("td")).map((td) =>
      td.textContent.trim(),
    );

    const pane = document.createElement("div");
    pane.className = "size-chart__pane";
    pane.id = `size-${id}`;
    pane.setAttribute("role", "tabpanel");
    pane.setAttribute("aria-labelledby", id);
    const isActive = idx === 0;
    pane.classList.toggle("is-active", isActive);
    pane.hidden = !isActive;
    pane.setAttribute("aria-hidden", String(!isActive));

    const t = document.createElement("table");
    const body = document.createElement("tbody");

    values.forEach((val, i) => {
      const tr = document.createElement("tr");
      const th = document.createElement("th");

      // Extract unit from measure name, e.g. "A: Pecho (cm)" → label "A: Pecho", unit "cm"
      const rawMeasure = measureNames[i] || "";
      const unitMatch = rawMeasure.match(/\(([^)]+)\)\s*$/);
      const unit = unitMatch ? unitMatch[1] : "";
      const cleanLabel = unitMatch
        ? rawMeasure.slice(0, unitMatch.index).trimEnd()
        : rawMeasure;

      th.textContent = cleanLabel.replace(":", " -");
      const td = document.createElement("td");
      td.textContent = unit ? `${val} ${unit}` : val;
      tr.appendChild(th);
      tr.appendChild(td);
      body.appendChild(tr);
    });

    t.appendChild(body);
    pane.appendChild(t);
    frag.appendChild(pane);
  });

  contentContainer.appendChild(frag);
};

window.sizeChartHelpers = {
  initSizeChartTabsFromSource,
  buildSizeChartPanesFromSource,
  initSizeChartTabs,
};
